// src/components/features/ParkingSessions/ParkingSessionCard.tsx
//
// Parking session card component — redesigned to match RecordCard layout:
//
// Desktop (sm+):
//   - Left column: colored status indicator circle (like RecordCard type indicator)
//   - Right column: compact header row (time range · car · duration), content, footer
//   - Price in the top-right corner
//   - Edit/Delete/End action buttons appear on hover (top-right, next to price)
//   - No footer action bar
//
// Mobile (<sm):
//   - Full-width vertical stack (no status column)
//   - Swipe actions for edit/delete/end
//   - Status shown as colored badge

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Group,
  Text,
  Stack,
  Box,
  Badge,
  Tooltip,
  Skeleton,
  ActionIcon,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";

dayjs.extend(relativeTime);
dayjs.extend(utc);

import {
  ParkingSession,
  isSessionActive,
  isSessionExpired,
  getSessionRemainingMinutes,
  getSessionElapsedMinutes,
} from "@/utils/api";
import {
  AppIcon,
  SwipeableCard,
  SwipeAction,
} from "@/components/generic";

// =============================================================================
// Types
// =============================================================================

export interface ParkingSessionCardProps {
  /** Parking session data */
  session: ParkingSession;
  /** Callback when edit action is clicked */
  onEdit?: (session: ParkingSession) => void;
  /** Callback when delete action is clicked */
  onDelete?: (session: ParkingSession) => void;
  /** Callback when end session is clicked */
  onEnd?: (session: ParkingSession) => void;
  /** Callback when card is clicked */
  onClick?: (session: ParkingSession) => void;
  /** Whether this card should be visually highlighted */
  highlighted?: boolean;
  /** Whether this session is currently being deleted */
  isDeleting?: boolean;
  /** Whether this session is currently being ended */
  isEnding?: boolean;
  /** Skip entrance animation (for already-rendered cards) */
  skipAnimation?: boolean;
  /** Currency formatting function */
  formatCurrency?: (amount: number, currency?: string) => string;

  // Swipe management props (mobile)
  swipeable?: boolean;
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

// =============================================================================
// Constants
// =============================================================================

const ACTIVE_COLOR = "green";
const COMPLETED_COLOR = "gray";
const EXPIRED_COLOR = "red";

// =============================================================================
// Animation Variants
// =============================================================================

const pulseVariants = {
  pulse: {
    scale: [1, 1.4, 1],
    opacity: [1, 0.6, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut" as const,
    },
  },
};

// =============================================================================
// CSS for hover-reveal actions (injected once, matching RecordCard approach)
// =============================================================================

const ACTIONS_CLASS = "parking-card__actions";
const CARD_CLASS = "parking-card";

const CARD_STYLES = `
  .${ACTIONS_CLASS} {
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms ease;
  }

  /* Mouse hover — only on devices that support hover */
  @media (hover: hover) {
    .${CARD_CLASS}:hover .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }

  /* Keyboard focus-within — always accessible */
  .${CARD_CLASS}:focus-within .${ACTIONS_CLASS} {
    opacity: 1;
    pointer-events: auto;
  }

  /* Touch devices (no hover) — always show actions */
  @media (hover: none) {
    .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

let stylesInjected = false;
const injectStyles = () => {
  if (stylesInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.setAttribute("data-parking-card", "");
  style.textContent = CARD_STYLES;
  document.head.appendChild(style);
  stylesInjected = true;
};

// =============================================================================
// Helpers
// =============================================================================

/**
 * Format duration in minutes to a human-readable string.
 * e.g. 90 → "1h 30m", 45 → "45m", 150 → "2h 30m"
 */
function formatDurationMinutes(
  minutes: number | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (minutes == null || minutes < 0) return "-";

  if (minutes < 1) {
    return t("parkingSessions:duration.lessThanMinute", {
      defaultValue: "< 1m",
    });
  }

  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);

  if (hours === 0) {
    return t("parkingSessions:duration.minutes", {
      count: mins,
      defaultValue: "{{count}}m",
    });
  }

  if (mins === 0) {
    return t("parkingSessions:duration.hours", {
      count: hours,
      defaultValue: "{{count}}h",
    });
  }

  return t("parkingSessions:duration.hoursMinutes", {
    hours,
    minutes: mins,
    defaultValue: "{{hours}}h {{minutes}}m",
  });
}

/**
 * Format the time range display for a session.
 */
function formatTimeRange(session: ParkingSession): string {
  if (!session.startTime) return "-";

  const startDayjs = dayjs(session.startTime);
  const start = startDayjs.format("MMM DD, YYYY · HH:mm");

  if (session.endTime) {
    const end = dayjs(session.endTime);
    // Same day: show just end time
    if (startDayjs.isSame(end, "day")) {
      return `${start} → ${end.format("HH:mm")}`;
    }
    // Different day: show full end date
    return `${start} → ${end.format("MMM DD, YYYY · HH:mm")}`;
  }

  return start;
}

// =============================================================================
// Styles
// =============================================================================

const componentStyles = {
  // Desktop status indicator column (matches RecordCard typeIndicator)
  statusIndicator: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center" as const,
    gap: 4,
    flexShrink: 0,
    width: 56,
    paddingTop: 2,
  },
  // Desktop status icon circle (matches RecordCard typeIconCircle)
  statusIconCircle: (color: string) => ({
    width: 44,
    height: 44,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center" as const,
    justifyContent: "center" as const,
    backgroundColor: `var(--mantine-color-${color}-0)`,
    border: `2px solid var(--mantine-color-${color}-2)`,
    flexShrink: 0,
  }),
  cardPaper: {
    cursor: "pointer",
    transition: "box-shadow 150ms ease, opacity 150ms ease",
  },
};

// =============================================================================
// Loading Skeleton (matches RecordCard skeleton structure)
// =============================================================================

export const ParkingSessionCardSkeleton: React.FC = () => (
  <Paper p="md" withBorder>
    <Group gap="sm" wrap="nowrap" align="flex-start">
      {/* Status indicator placeholder */}
      <Box pt={4}>
        <Skeleton height={14} width={14} circle />
      </Box>

      {/* Content */}
      <Stack gap="md" style={{ flex: 1 }}>
        {/* Date header */}
        <Skeleton height={24} width={200} />

        {/* Vehicle + duration row */}
        <Group gap="md">
          <Group gap={6}>
            <Skeleton height={18} width={18} radius="sm" />
            <Skeleton height={18} width={90} />
          </Group>
          <Group gap={6}>
            <Skeleton height={18} width={18} radius="sm" />
            <Skeleton height={18} width={70} />
          </Group>
        </Group>

        {/* Location */}
        <Group gap={8} wrap="nowrap" align="flex-start">
          <Skeleton height={18} width={18} radius="sm" style={{ flexShrink: 0 }} />
          <Skeleton height={18} width="60%" />
        </Group>

        {/* Price */}
        <Group justify="flex-end">
          <Skeleton height={28} width={80} />
        </Group>
      </Stack>
    </Group>
  </Paper>
);

// =============================================================================
// Component
// =============================================================================

const ParkingSessionCardComponent: React.FC<ParkingSessionCardProps> = ({
  session,
  onEdit,
  onDelete,
  onEnd,
  onClick,
  highlighted = false,
  isDeleting = false,
  isEnding = false,
  formatCurrency,
  //swipeable = false,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["parkingSessions", "common"]);

  // Inject CSS for hover/focus action visibility (once)
  injectStyles();

  const active = isSessionActive(session);
  const expired = isSessionExpired(session);
  const isProcessing = isDeleting || isEnding;

  // Determine status color
  const statusColor = active
    ? expired
      ? EXPIRED_COLOR
      : ACTIVE_COLOR
    : COMPLETED_COLOR;

  // Status icon name for the desktop indicator circle
  const statusIconName = active ? "Parking" : "CircleCheck";

  // Elapsed / remaining time
  const elapsedMinutes = useMemo(
    () => getSessionElapsedMinutes(session),
    [session]
  );
  const remainingMinutes = useMemo(
    () => getSessionRemainingMinutes(session),
    [session]
  );

  // Format price
  const priceDisplay = useMemo(() => {
    const price = session.finalPrice ?? session.initialPrice;
    if (price == null || price === 0) {
      return t("parkingSessions:card.free", { defaultValue: "Free" });
    }
    if (formatCurrency) {
      return formatCurrency(price, session.currency || undefined);
    }
    const curr = session.currency || "";
    return `${curr} ${price.toFixed(2)}`.trim();
  }, [session.finalPrice, session.initialPrice, session.currency, formatCurrency, t]);

  // Whether price is non-zero
  const hasPaidPrice = useMemo(() => {
    const price = session.finalPrice ?? session.initialPrice;
    return price != null && price > 0;
  }, [session.finalPrice, session.initialPrice]);

  // Duration display value
  const durationDisplay = useMemo(() => {
    if (active && elapsedMinutes != null) {
      return formatDurationMinutes(elapsedMinutes, t);
    }
    if (!active) {
      const dur = session.durationMinutes ?? elapsedMinutes;
      if (dur != null) return formatDurationMinutes(dur, t);
    }
    return null;
  }, [active, elapsedMinutes, session.durationMinutes, t]);

  // Remaining time label for active paid sessions
  const remainingLabel = useMemo(() => {
    if (!active || remainingMinutes == null || remainingMinutes <= 0) return null;
    return `${formatDurationMinutes(remainingMinutes, t)} ${t("parkingSessions:card.remaining", { defaultValue: "left" })}`;
  }, [active, remainingMinutes, t]);

  const hasActions = onEdit || onDelete || (active && onEnd);

  // ============================================
  // Event Handlers
  // ============================================

  const handleCardClick = useCallback(() => {
    if (!isProcessing && onClick) onClick(session);
  }, [isProcessing, onClick, session]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.target !== event.currentTarget) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick?.(session);
      }
    },
    [onClick, session]
  );

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEdit?.(session);
    },
    [onEdit, session]
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.(session);
    },
    [onDelete, session]
  );

  const handleEndClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEnd?.(session);
    },
    [onEnd, session]
  );

  const handleSwipeStateChange = useCallback(
    (isOpen: boolean) => onSwipeStateChange?.(isOpen),
    [onSwipeStateChange]
  );

  // ============================================
  // Swipe Actions (mobile only)
  // ============================================

  const leftSwipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (active && onEnd) {
      actions.push({
        key: "end",
        icon: "SquareCheck",
        label: t("parkingSessions:actions.endSession", { defaultValue: "End" }),
        color: "teal",
        onClick: () => onEnd(session),
        disabled: isProcessing,
        loading: isEnding,
      });
    }

    if (onEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(session),
        disabled: isProcessing,
      });
    }

    if (onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(session),
        loading: isDeleting,
        disabled: isProcessing,
      });
    }

    return actions;
  }, [active, isProcessing, isDeleting, isEnding, onEnd, onEdit, onDelete, session, t]);

  // ============================================
  // Desktop hover actions (matching RecordCard pattern)
  // ============================================

  const hoverActionsEl = hasActions && (
    <Group gap={4} wrap="nowrap" className={ACTIONS_CLASS}>
      {active && onEnd && (
        <Tooltip
          label={t("parkingSessions:actions.endSession", { defaultValue: "End Session" })}
          withArrow
          position="bottom"
        >
          <ActionIcon
            variant="subtle"
            color="teal"
            size="md"
            onClick={handleEndClick}
            disabled={isProcessing}
            loading={isEnding}
            aria-label={t("parkingSessions:actions.endSession", { defaultValue: "End Session" })}
          >
            <AppIcon iconName="SquareCheck" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={handleEditClick}
            disabled={isProcessing}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={handleDeleteClick}
            loading={isDeleting}
            disabled={isProcessing}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );

  // ============================================
  // Shared content sections (used by both layouts)
  // ============================================

  const durationSection = durationDisplay && (
    <Group gap={6}>
      <AppIcon
        iconName="Hourglass"
        size={18}
        color={active && expired ? "red" : active ? "blue" : "gray"}
      />
      <Text
        size="md"
        c={active && expired ? "red" : active ? "blue" : "dimmed"}
        fw={active ? 500 : undefined}
      >
        {durationDisplay}
      </Text>
      {remainingLabel && (
        <Text size="sm" c="dimmed">
          ({remainingLabel})
        </Text>
      )}
    </Group>
  );

  const locationSection = session.formattedAddress && (
    <Group gap={8} wrap="nowrap" align="flex-start">
      <AppIcon
        iconName="MapPin"
        size={18}
        color="gray"
        style={{ flexShrink: 0, marginTop: 3 }}
      />
      <Text size="md" c="dimmed" lineClamp={2}>
        {session.formattedAddress}
      </Text>
    </Group>
  );

  const notesSection = session.notes && (
    <Group gap={8} wrap="nowrap" align="flex-start">
      <AppIcon
        iconName="Notes"
        size={18}
        color="gray"
        style={{ flexShrink: 0, marginTop: 3 }}
      />
      <Text size="md" c="dimmed" lineClamp={1} fs="italic">
        {session.notes}
      </Text>
    </Group>
  );

  const travelSection = session.travel && (
    <Group gap={6}>
      <AppIcon iconName="Route" size={18} color="gray" />
      <Text size="md" c="dimmed" lineClamp={1}>
        {session.travel.destination || session.travel.purpose}
      </Text>
    </Group>
  );

  const userSection = session.userStarted && (
    <Group gap={6}>
      <AppIcon iconName="User" size={18} color="gray" />
      <Text size="md" c="dimmed" lineClamp={1}>
        {[session.userStarted.firstName, session.userStarted.lastName]
          .filter(Boolean)
          .join(" ")}
      </Text>
    </Group>
  );

  // ============================================
  // MOBILE LAYOUT (<sm)
  // ============================================

  const mobileLayout = (
    <Box hiddenFrom="sm">
      <Stack gap="sm" style={{ minWidth: 0 }}>
        {/* Header: time range + status badge */}
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Text size="md" fw={600} lineClamp={1}>
            {formatTimeRange(session)}
          </Text>
          {active && (
            <Badge
              size="xs"
              variant="light"
              color={expired ? "red" : "green"}
              style={{ flexShrink: 0 }}
            >
              {expired
                ? t("parkingSessions:status.expired", { defaultValue: "Expired" })
                : t("parkingSessions:status.active", { defaultValue: "Active" })}
            </Badge>
          )}
        </Group>

        {/* Car + Duration */}
        <Group gap="md" wrap="wrap">
          {session.car && (
            <Group gap={6}>
              <AppIcon iconName="Car" size={16} color="gray" />
              <Text size="md" c="dimmed">{session.car.label}</Text>
            </Group>
          )}
          {durationSection}
        </Group>

        {/* Travel link */}
        {travelSection}

        {/* User */}
        {userSection}

        {/* Location */}
        {locationSection}

        {/* Notes */}
        {notesSection}

        {/* Price */}
        <Group justify="flex-end">
          <Text fw={700} size="lg" c={hasPaidPrice ? "green.7" : "dimmed"}>
            {priceDisplay}
          </Text>
        </Group>
      </Stack>
    </Box>
  );

  // ============================================
  // DESKTOP LAYOUT (sm+) — matching RecordCard structure
  // ============================================

  const desktopLayout = (
    <Box visibleFrom="sm">
      <Group gap="md" wrap="nowrap" align="flex-start">
        {/* Status indicator column (like RecordCard type indicator) */}
        <Box style={componentStyles.statusIndicator}>
          <Tooltip
            label={
              active && expired
                ? t("parkingSessions:status.expired", { defaultValue: "Expired" })
                : active
                  ? t("parkingSessions:status.active", { defaultValue: "Active" })
                  : t("parkingSessions:status.completed", { defaultValue: "Completed" })
            }
            withArrow
          >
            <Box style={componentStyles.statusIconCircle(statusColor)}>
              {active ? (
                <motion.div
                  variants={expired ? undefined : pulseVariants}
                  animate={expired ? undefined : "pulse"}
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: expired
                      ? "var(--mantine-color-red-5)"
                      : "var(--mantine-color-green-5)",
                  }}
                />
              ) : (
                <AppIcon iconName={statusIconName} size={22} color={statusColor} />
              )}
            </Box>
          </Tooltip>
          {active && (
            <Text size="md" fw={600} c={statusColor} ta="center" lh={1.2}>
              {expired
                ? t("parkingSessions:status.expired", { defaultValue: "Expired" })
                : t("parkingSessions:status.active", { defaultValue: "Active" })}
            </Text>
          )}
        </Box>

        {/* Main content column */}
        <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
          {/* Row 1: Time range · Car · Duration    [Hover Actions] [Price] */}
          <Group justify="space-between" wrap="nowrap">
            <Group gap="lg" wrap="wrap" style={{ flex: 1 }}>
              {/* Time range */}
              <Text size="md" fw={600}>
                {formatTimeRange(session)}
              </Text>

              {/* Car */}
              {session.car && (
                <Group gap={6}>
                  <AppIcon iconName="Car" size={18} color="gray" />
                  <Text size="md" c="dimmed">{session.car.label}</Text>
                </Group>
              )}

              {/* Duration */}
              {durationSection}
            </Group>

            {/* Right side: hover actions + price */}
            <Group gap="md" wrap="nowrap" style={{ flexShrink: 0 }}>
              {hoverActionsEl}
              <Text fw={700} size="xl" c={hasPaidPrice ? "green.7" : "dimmed"}>
                {priceDisplay}
              </Text>
            </Group>
          </Group>

          {/* Travel, user, location, notes — horizontal footer */}
          {(travelSection || userSection || locationSection || notesSection) && (
            <Group gap="lg" wrap="wrap" align="flex-start">
              {travelSection}
              {userSection}
              {locationSection}
              {notesSection}
            </Group>
          )}
        </Stack>
      </Group>
    </Box>
  );

  // ============================================
  // Card Paper wrapper
  // ============================================

  const cardContent = (
    <Paper
      p="md"
      withBorder
      bg={highlighted ? "green.0" : undefined}
      opacity={isDeleting ? 0.5 : 1}
      className={CARD_CLASS}
      style={{
        ...componentStyles.cardPaper,
        cursor: onClick ? "pointer" : undefined,
      }}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? "button" : undefined}
    >
      {mobileLayout}
      {desktopLayout}
    </Paper>
  );

  // ============================================
  // Mobile: wrap with SwipeableCard
  // Desktop: render plain card (actions on hover)
  // ============================================

  if (leftSwipeActions.length > 0) {
    return (
      <>
        {/* Mobile: swipeable */}
        <Box hiddenFrom="sm">
          <SwipeableCard
            leftSwipeActions={leftSwipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={handleSwipeStateChange}
            disabled={isProcessing}
            borderRadius="var(--mantine-radius-default)"
          >
            {/* Re-render mobile-only Paper since SwipeableCard wraps it */}
            <Paper
              p="md"
              withBorder
              bg={highlighted ? "green.0" : undefined}
              opacity={isDeleting ? 0.5 : 1}
              style={{
                cursor: onClick ? "pointer" : undefined,
                transition: "opacity 150ms ease",
              }}
              onClick={handleCardClick}
              onKeyDown={handleKeyDown}
              tabIndex={onClick ? 0 : undefined}
              role={onClick ? "button" : undefined}
            >
              {mobileLayout}
            </Paper>
          </SwipeableCard>
        </Box>

        {/* Desktop: plain card with hover actions */}
        <Box visibleFrom="sm">{cardContent}</Box>
      </>
    );
  }

  return cardContent;
};

export const ParkingSessionCard = React.memo(ParkingSessionCardComponent);